/**
 * Manufacturer-direct financing — third option alongside cash purchase and leasing.
 *
 * Each truck brand has a captive finance arm with its own identity, base rate
 * and loyalty program. The proposed rate is ALWAYS more expensive than a bank
 * loan (the convenience premium), but procedural friction is lower (no credit
 * scoring, no debt-ratio gate) and a brand-loyalty discount kicks in after a
 * few purchases. Defaulting on a manufacturer loan damages the relationship
 * and locks future offers from that brand.
 *
 * Mechanics:
 *   - rate = baseRate − loyaltyDiscount[loyaltyLevel]   (floor 2.0 %)
 *   - durationOptions: 36 / 48 / 60 months
 *   - down payment: 10 % of vehicle price required up front
 *   - loyalty tiers (units financed):
 *       0    → 'Prospect'              0 pp discount
 *       1-2  → 'Client régulier'       0.2 pp
 *       3-5  → 'Client privilégié'     0.5 pp + 5 % rebate on residual
 *       6+   → 'Partenaire stratégique' 0.8 pp + 10 % rebate + invitation salons
 *
 * Brand parameters are designed so the player has a meaningful comparison:
 *   VALREK   = utilities specialist, lowest base rate
 *   MANTOR   = premium tractor brand, mid rate but generous loyalty
 *   IVECTRA  = popular all-rounder, mid rate
 *   SCANEX   = high-end, highest rate but best loyalty perks
 *   VOLVAX   = premium frigo, mid-high rate
 *   DAFORA   = entry-level, low rate but no loyalty bonus past tier 1
 */

export const MANUFACTURER_FINANCE = {
  VALREK: {
    id: 'VALREK',
    name: 'VALREK Finance',
    color: '#1E40AF',
    tagline: 'L\'utilitaire qui vous suit, le financement qui vous comprend.',
    baseRate: 0.058,
    downPaymentPct: 0.10,
    loyaltyTiers: [
      { units: 0, label: 'Prospect',                 discount: 0,     residualRebate: 0 },
      { units: 1, label: 'Client régulier',          discount: 0.002, residualRebate: 0 },
      { units: 3, label: 'Client privilégié',        discount: 0.005, residualRebate: 0.05 },
      { units: 6, label: 'Partenaire stratégique',   discount: 0.008, residualRebate: 0.10 },
    ],
    welcomeMessageId: 'mfg-welcome-valrek',
  },
  MANTOR: {
    id: 'MANTOR',
    name: 'MANTOR Captive Finance',
    color: '#0F766E',
    tagline: 'Le tracteur premium, financé sur mesure.',
    baseRate: 0.068,
    downPaymentPct: 0.10,
    loyaltyTiers: [
      { units: 0, label: 'Prospect',                 discount: 0,     residualRebate: 0 },
      { units: 1, label: 'Client régulier',          discount: 0.003, residualRebate: 0 },
      { units: 3, label: 'Client privilégié',        discount: 0.007, residualRebate: 0.06 },
      { units: 6, label: 'Partenaire stratégique',   discount: 0.011, residualRebate: 0.12 },
    ],
    welcomeMessageId: 'mfg-welcome-mantor',
  },
  IVECTRA: {
    id: 'IVECTRA',
    name: 'IVECTRA Capital',
    color: '#DC2626',
    tagline: 'Roulez aujourd\'hui, payez à votre rythme.',
    baseRate: 0.064,
    downPaymentPct: 0.10,
    loyaltyTiers: [
      { units: 0, label: 'Prospect',                 discount: 0,     residualRebate: 0 },
      { units: 1, label: 'Client régulier',          discount: 0.002, residualRebate: 0 },
      { units: 3, label: 'Client privilégié',        discount: 0.005, residualRebate: 0.05 },
      { units: 6, label: 'Partenaire stratégique',   discount: 0.008, residualRebate: 0.10 },
    ],
    welcomeMessageId: 'mfg-welcome-ivectra',
  },
  SCANEX: {
    id: 'SCANEX',
    name: 'SCANEX Premium Finance',
    color: '#7C3AED',
    tagline: 'L\'excellence scandinave, une relation pour la vie.',
    baseRate: 0.072,
    downPaymentPct: 0.10,
    loyaltyTiers: [
      { units: 0, label: 'Prospect',                 discount: 0,     residualRebate: 0 },
      { units: 1, label: 'Client régulier',          discount: 0.003, residualRebate: 0 },
      { units: 3, label: 'Client privilégié',        discount: 0.008, residualRebate: 0.08 },
      { units: 6, label: 'Partenaire stratégique',   discount: 0.013, residualRebate: 0.15 },
    ],
    welcomeMessageId: 'mfg-welcome-scanex',
  },
  VOLVAX: {
    id: 'VOLVAX',
    name: 'VOLVAX Cool Capital',
    color: '#0891B2',
    tagline: 'La chaîne du froid, sans casser la trésorerie.',
    baseRate: 0.066,
    downPaymentPct: 0.10,
    loyaltyTiers: [
      { units: 0, label: 'Prospect',                 discount: 0,     residualRebate: 0 },
      { units: 1, label: 'Client régulier',          discount: 0.002, residualRebate: 0 },
      { units: 3, label: 'Client privilégié',        discount: 0.006, residualRebate: 0.06 },
      { units: 6, label: 'Partenaire stratégique',   discount: 0.010, residualRebate: 0.12 },
    ],
    welcomeMessageId: 'mfg-welcome-volvax',
  },
  DAFORA: {
    id: 'DAFORA',
    name: 'DAFORA Quick Finance',
    color: '#F59E0B',
    tagline: 'Simple, rapide, sans bla-bla.',
    baseRate: 0.060,
    downPaymentPct: 0.10,
    // DAFORA has a deliberately FLAT loyalty curve: only 2 tiers vs 4 for
    // premium brands. By design, it positions as "entry-level / no long-term
    // commitment", so the captive finance arm doesn't differentiate at units≥3.
    loyaltyTiers: [
      { units: 0, label: 'Prospect',           discount: 0,     residualRebate: 0 },
      { units: 1, label: 'Client régulier',    discount: 0.002, residualRebate: 0 },
    ],
    welcomeMessageId: 'mfg-welcome-dafora',
  },
};

export const DURATION_OPTIONS = [36, 48, 60];

/**
 * 18/06/2026 — demande user : « le taux d'emprunt devrait varier selon la
 * durée choisie ». Barème réaliste validé : plus la durée est longue, plus
 * le taux monte (risque constructeur).
 */
export const DURATION_RATE_SPREAD = { 36: 0, 48: 0.004, 60: 0.008 };

/**
 * 18/06/2026 — demande user : « gestes commerciaux supplémentaires selon
 * le niveau de relation client ». Par index de palier fidélité :
 *   0 Prospect               → apport 10 %
 *   1 Client régulier        → apport réduit à 8 %
 *   2 Client privilégié      → apport 6 % + 1ère mensualité OFFERTE
 *   3 Partenaire stratégique → apport 5 % + 1ère mensualité offerte
 *                              + baisse du taux de 0,5 pt (validé user)
 */
export const TIER_COMMERCIAL_GESTURES = [
  { downPaymentPct: 0.10, firstMonthFree: false, extraRateDiscount: 0 },
  { downPaymentPct: 0.08, firstMonthFree: false, extraRateDiscount: 0 },
  { downPaymentPct: 0.06, firstMonthFree: true,  extraRateDiscount: 0 },
  { downPaymentPct: 0.05, firstMonthFree: true,  extraRateDiscount: 0.005 },
];

/**
 * Get the loyalty tier for a given manufacturer relation (or freshly start).
 * @param {string} brand
 * @param {number} units
 * @returns {{label, discount, residualRebate, units, nextTier}}
 */
export const getManufacturerLoyaltyTier = (brand, units = 0) => {
  const m = MANUFACTURER_FINANCE[brand];
  if (!m) return null;
  let tier = m.loyaltyTiers[0];
  let tierIndex = 0;
  let nextTier = null;
  for (let i = 0; i < m.loyaltyTiers.length; i++) {
    if (units >= m.loyaltyTiers[i].units) {
      tier = m.loyaltyTiers[i];
      tierIndex = i;
      nextTier = m.loyaltyTiers[i + 1] || null;
    }
  }
  return { ...tier, tierIndex, nextTier };
};

/**
 * Compute a manufacturer finance quote for a given vehicle + brand + duration.
 * Returns null when the brand has no captive finance arm.
 * Returns { eligible:false, reason } when the player has a defaulted relation.
 * Returns the full quote on success.
 */
export const computeManufacturerQuote = (vehicle, brand, durationMonths, relation = null) => {
  const m = MANUFACTURER_FINANCE[brand];
  if (!m) return null;
  if (relation && relation.defaulted) {
    return { eligible: false, reason: 'Relation rompue suite à un défaut de paiement passé', brand };
  }
  const units = (relation && relation.units) || 0;
  const tier = getManufacturerLoyaltyTier(brand, units);
  // 18/06/2026 — taux par durée + gestes commerciaux par palier fidélité.
  const gestures = TIER_COMMERCIAL_GESTURES[Math.min(tier?.tierIndex || 0, TIER_COMMERCIAL_GESTURES.length - 1)];
  const durationSpread = DURATION_RATE_SPREAD[durationMonths] ?? 0;
  const rate = Math.max(0.02, m.baseRate + durationSpread - (tier?.discount || 0) - (gestures.extraRateDiscount || 0));
  const downPaymentPct = gestures.downPaymentPct ?? m.downPaymentPct;
  const downPayment = Math.round((vehicle.price || 0) * downPaymentPct);
  const financed = (vehicle.price || 0) - downPayment;
  // Standard annuity formula (monthly capitalisation).
  const r = rate / 12;
  const n = durationMonths;
  const monthly = financed * r / (1 - Math.pow(1 + r, -n));
  const firstMonthFree = !!gestures.firstMonthFree;
  const paidMonths = n - (firstMonthFree ? 1 : 0);
  return {
    eligible: true,
    brand,
    brandName: m.name,
    brandColor: m.color,
    tagline: m.tagline,
    tier: tier?.label || 'Prospect',
    tierIndex: tier?.tierIndex || 0,
    rate,
    ratePercent: (rate * 100).toFixed(2),
    discount: tier?.discount || 0,
    extraRateDiscount: gestures.extraRateDiscount || 0,
    residualRebate: tier?.residualRebate || 0,
    durationMonths,
    durationSpread,
    downPaymentPct,
    downPayment,
    firstMonthFree,
    financed,
    monthly: Math.round(monthly),
    totalCost: Math.round(monthly * paidMonths + downPayment),
    totalInterest: Math.round(monthly * paidMonths - financed),
    nextTier: tier?.nextTier || null,
    unitsToNextTier: tier?.nextTier ? Math.max(0, tier.nextTier.units - units) : null,
  };
};
